/**
 * ADM-030. Engine staff edit ingredients: form, daily limits, which pharmacy can make them, and
 * whether they blend. A save is audited with the reason they give.
 */
import * as stylex from "@stylexjs/stylex";
import type { AdminCatalog, AdminIngredient, AdminIngredientStatus } from "@lattice/core";
import { ROUTES } from "@lattice/core";
import "@lattice/tokens/space.stylex";
import { space } from "@lattice/tokens/space.stylex";
import { useState } from "react";
import { api } from "./api.ts";
import type { Column } from "./layout.tsx";
import { PageHead, Panel, Table } from "./layout.tsx";
import { href } from "./router.ts";
import { useLoad } from "./load.ts";
import { ReasonForm } from "./ReasonForm.tsx";
import { Check, ErrorText, Field, Link, Select, Status } from "./ui.tsx";
import { refusal } from "./words.ts";
interface Draft {
  readonly id: string;
  readonly name: string;
  readonly form: string;
  readonly unit: string;
  readonly dailyMin: string;
  readonly dailyMax: string;
  readonly blendable: boolean;
  readonly isProteinBase: boolean;
  readonly pharmacyPartnerId: string;
  readonly addonPriceCents: string;
  readonly status: AdminIngredientStatus;
}
const EMPTY: Draft = {
  id: "",
  name: "",
  form: "powder",
  unit: "",
  dailyMin: "",
  dailyMax: "",
  blendable: true,
  isProteinBase: false,
  pharmacyPartnerId: "",
  addonPriceCents: "",
  status: "active"
};
function fromIngredient(row: AdminIngredient): Draft {
  return {
    id: row.id,
    name: row.name,
    form: row.form,
    unit: row.unit,
    dailyMin: String(row.dailyMin),
    dailyMax: String(row.dailyMax),
    blendable: row.blendable,
    isProteinBase: row.isProteinBase,
    pharmacyPartnerId: row.pharmacyPartnerId ?? "",
    addonPriceCents: row.addonPriceCents === null ? "" : String(row.addonPriceCents),
    status: row.status
  };
}
function money(cents: number | null): string {
  return cents === null ? "—" : `$${(cents / 100).toFixed(2)}`;
}
const COLUMNS: readonly Column<AdminIngredient>[] = [{
  label: "Ingredient",
  cell: row => <Link to={href.ingredient(row.id)}>{row.name}</Link>
}, {
  label: "Form",
  nowrap: true,
  cell: row => row.form
}, {
  label: "Daily range",
  phoneLabel: true,
  cell: row => `${row.dailyMin}–${row.dailyMax} ${row.unit}`
}, {
  label: "Blend",
  nowrap: true,
  cell: row => row.blendable ? "Blends" : "Ships separately"
}, {
  label: "Pharmacy",
  phoneLabel: true,
  cell: row => row.pharmacyName ?? "Any"
}, {
  label: "Add-on",
  numeric: true,
  phoneLabel: true,
  cell: row => money(row.addonPriceCents)
}, {
  label: "Status",
  nowrap: true,
  cell: row => row.status === "active" ? <Status tone="ok">Active</Status> : <Status tone="neutral">Retired</Status>
}];
function bodyOf(draft: Draft) {
  const addon = draft.addonPriceCents.trim();
  return {
    name: draft.name,
    form: draft.form,
    unit: draft.unit,
    dailyMin: Number(draft.dailyMin),
    dailyMax: Number(draft.dailyMax),
    blendable: draft.blendable,
    isProteinBase: draft.isProteinBase,
    pharmacyPartnerId: draft.pharmacyPartnerId === "" ? null : draft.pharmacyPartnerId,
    addonPriceCents: addon === "" ? null : Number(addon),
    status: draft.status
  };
}
function Editor({
  catalog,
  selected,
  draft,
  onDraft,
  onSaved
}: {
  readonly catalog: AdminCatalog;
  readonly selected: string | null;
  readonly draft: Draft;
  readonly onDraft: (draft: Draft) => void;
  readonly onSaved: (next: AdminCatalog) => void;
}) {
  const creating = selected === null;
  const pharmacies = [{
    value: "",
    label: "Any pharmacy"
  }, ...catalog.pharmacies.map(row => ({
    value: row.id,
    label: row.name
  }))];
  async function save(reason: string): Promise<string | null> {
    const body = {
      ...bodyOf(draft),
      reason
    };
    const result = creating ? await api<AdminCatalog>(ROUTES.adminCreateIngredient, {
      body: {
        ...body,
        id: draft.id
      }
    }) : await api<AdminCatalog>(ROUTES.adminUpdateIngredient, {
      params: {
        ingredientId: draft.id
      },
      body
    });
    if (!result.ok) return refusal(result.error);
    onSaved(result.data);
    return null;
  }
  return <Panel padded>
      <div className="x78zum5 xdt5ytf x1r0x45p">
        <ReasonForm id="ingredient-reason" hint="What changed, and why." action={creating ? "Add ingredient" : "Save ingredient"} onSubmit={save}>
          {creating ? <Field id="ingredient-id" label="Id" hint="Lowercase, numbers and underscores. Used in rules." value={draft.id} onChange={id => onDraft({
          ...draft,
          id
        })} /> : null}
          <Field id="ingredient-name" label="Name" value={draft.name} onChange={name => onDraft({
          ...draft,
          name
        })} />
          <Field id="ingredient-form" label="Form" value={draft.form} onChange={form => onDraft({
          ...draft,
          form
        })} />
          <Field id="ingredient-unit" label="Unit" value={draft.unit} onChange={unit => onDraft({
          ...draft,
          unit
        })} />
          <Field id="ingredient-min" label="Daily minimum" type="number" value={draft.dailyMin} onChange={dailyMin => onDraft({
          ...draft,
          dailyMin
        })} />
          <Field id="ingredient-max" label="Daily maximum" type="number" value={draft.dailyMax} onChange={dailyMax => onDraft({
          ...draft,
          dailyMax
        })} />
          <Select id="ingredient-pharmacy" label="Pharmacy" value={draft.pharmacyPartnerId} onChange={pharmacyPartnerId => onDraft({
          ...draft,
          pharmacyPartnerId
        })} options={pharmacies} />
          <Field id="ingredient-addon" label="Add-on price, cents" hint="Leave blank when it is part of the blend." type="number" value={draft.addonPriceCents} onChange={addonPriceCents => onDraft({
          ...draft,
          addonPriceCents
        })} />
          <Select id="ingredient-status" label="Status" value={draft.status} onChange={status => onDraft({
          ...draft,
          status: status === "retired" ? "retired" : "active"
        })} options={[{
          value: "active",
          label: "Active"
        }, {
          value: "retired",
          label: "Retired"
        }]} />
          <div className="x78zum5 xdt5ytf x1173whf">
            <Check id="ingredient-blendable" label="The pharmacy can blend this into the powder" checked={draft.blendable} onChange={blendable => onDraft({
            ...draft,
            blendable
          })} />
            <Check id="ingredient-protein" label="This is a protein base" checked={draft.isProteinBase} onChange={isProteinBase => onDraft({
            ...draft,
            isProteinBase
          })} />
          </div>
        </ReasonForm>
      </div>
    </Panel>;
}
function List({
  catalog
}: {
  readonly catalog: AdminCatalog;
}) {
  return <>
      <PageHead title="Ingredient catalog" meta={`${catalog.ingredients.length} ingredients`} actions={<Link to={href.newIngredient()}>Add ingredient</Link>} />
      <Table label="Ingredients" columns={COLUMNS} rows={catalog.ingredients} rowKey={row => row.id} empty="No ingredients yet. Add one to start the catalog." />
    </>;
}
function Ingredient({
  catalog,
  id,
  onSaved
}: {
  readonly catalog: AdminCatalog;
  readonly id: string | null;
  readonly onSaved: (next: AdminCatalog) => void;
}) {
  const row = id === null ? null : catalog.ingredients.find(item => item.id === id) ?? null;
  const [draft, setDraft] = useState<Draft>(row === null ? EMPTY : fromIngredient(row));
  if (id !== null && row === null) {
    return <>
        <PageHead back={<Link to={href.catalog()}>Back to catalog</Link>} title="Ingredient" lead="That ingredient is not in the catalog." />
        <Link to={href.catalog()}>Back to catalog</Link>
      </>;
  }
  return <>
      <PageHead back={<Link to={href.catalog()}>Back to catalog</Link>} title={row === null ? "Add ingredient" : row.name} lead={row === null ? "A new ingredient starts inactive until you save it." : undefined} />
      <Editor catalog={catalog} selected={id} draft={draft} onDraft={setDraft} onSaved={onSaved} />
    </>;
}
export function Catalog({
  ingredient,
  creating
}: {
  readonly ingredient: string | null;
  readonly creating: boolean;
}) {
  const [found, setFound] = useLoad("catalog", () => api<AdminCatalog>(ROUTES.adminIngredients));
  if (found.state === "loading") return <PageHead title="Ingredient catalog" meta="Loading" />;
  if (found.state === "failed") return <ErrorText>{refusal(found.error)}</ErrorText>;
  if (creating || ingredient !== null) {
    return <Ingredient key={creating ? "new" : ingredient} catalog={found.data} id={creating ? null : ingredient} onSaved={setFound} />;
  }
  return <List catalog={found.data} />;
}